import { Task, WeeklyPlan, PhysicalUnit, Responsible, ProjectProgressSnapshot, ProtocolState, ProjectInfo } from '../types';

export interface SCurvePoint {
    name: string;
    date: string;
    weekIndex: number;
    plan: number;
    real: number | null;
}

/**
 * Calculates the S-Curve data (Planned vs Real progress over time)
 * @param tasks - List of project tasks
 * @returns Array of SCurvePoint for charting
 */
export const calculateSCurve = (tasks: Task[], progressHistory: ProjectProgressSnapshot[] = []): SCurvePoint[] => {
    const flatTasks: Task[] = [];
    const traverse = (t: Task[]) => t.forEach(task => {
        const hasSubtasks = task.children && task.children.some(c => c.isWeeklySubtask);
        if (task.type === 'task' && task.start && task.end) {
            flatTasks.push(task);
        }
        if (task.children && !hasSubtasks) {
            traverse(task.children);
        }
    });
    traverse(tasks);

    if (flatTasks.length === 0) return [];

    // Find global range
    const starts = flatTasks.map(t => new Date(t.start! + "T12:00:00").getTime());
    const ends = flatTasks.map(t => new Date(t.end! + "T12:00:00").getTime());
    const minStart = new Date(Math.min(...starts));
    const maxEnd = new Date(Math.max(...ends));

    // Generate weekly points
    const points: SCurvePoint[] = [];
    let curr = new Date(minStart);

    // Normalize to start of week (Monday)
    const day = curr.getDay();
    const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
    curr.setDate(diff);

    const totalProjectDuration = flatTasks.reduce((sum, t) => sum + (t.duration || 0), 0);
    const now = new Date();

    while (curr <= maxEnd || points.length < 4) {
        const dateStr = curr.toISOString().split('T')[0];
        const dateLabel = curr.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        const compareTime = curr.getTime();

        // 1. Calculate Planned Progress
        let plannedDurationAccum = 0;
        flatTasks.forEach(t => {
            const tStart = new Date(t.start! + "T12:00:00").getTime();
            const tEnd = new Date(t.end! + "T12:00:00").getTime();
            const tDur = t.duration || 0;

            if (compareTime >= tEnd) {
                plannedDurationAccum += tDur;
            } else if (compareTime > tStart) {
                const totalDays = Math.max(86400000, tEnd - tStart);
                const elapsedDays = compareTime - tStart;
                plannedDurationAccum += (elapsedDays / totalDays) * tDur;
            }
        });

        const planPct = totalProjectDuration > 0
            ? Number(truncateProgress((plannedDurationAccum / totalProjectDuration) * 100, 2))
            : 0;

        // 2. Calculate Real Progress (from history)
        let realPct: number | null = null;

        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            // Sort to be sure
            const validHistory = progressHistory.filter(h => h && h.date);
            if (validHistory.length > 0) {
                const sortedHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
                const lastRecord = sortedHistory[sortedHistory.length - 1];
                const lastRecordTime = new Date(lastRecord.date + "T23:59:59").getTime();

            // Only show real data up to and including the week of the last recorded sync
            if (compareTime <= lastRecordTime) {
                // Filter records up to the END of the current chart week
                const endOfCurrentWeekStr = new Date(compareTime + (7 * 86400000)).toISOString().split('T')[0];
                const pastOrCurrentRecords = sortedHistory.filter(r => r.date <= endOfCurrentWeekStr);

                if (pastOrCurrentRecords.length > 0) {
                    realPct = pastOrCurrentRecords[pastOrCurrentRecords.length - 1].progress;
                } else {
                    // Start from 0% at the beginning of the project
                    realPct = 0;
                }
            }
            }
        }

        points.push({
            name: dateLabel,
            date: dateStr, // Added for precise matching
            weekIndex: points.length + 1, // Added sequential week number
            plan: Math.min(100, planPct),
            real: realPct !== null ? Math.min(100, realPct) : null
        });

        curr.setDate(curr.getDate() + 7);
        if (points.length > 100) break; // Hard limit for safety
    }
    return points;
};

/**
 * Truncates a number to a specific number of decimal places without rounding.
 */
export const truncateProgress = (val: number, decimals: number = 2): string => {
    if (isNaN(val) || val === null) return (0).toFixed(decimals);
    const factor = Math.pow(10, decimals);
    // Use a small epsilon to avoid floating point errors like 1.000000000000000001
    return (Math.floor(val * factor + 0.0000001) / factor).toFixed(decimals);
};

export const isProtocolProgressed = (
    taskId: string,
    unitId: string,
    savedProtocols: Record<string, ProtocolState>,
    protocolConfigs: any
): boolean => {
    return Object.values(savedProtocols).some((p: ProtocolState) => {
        if (p.taskId === taskId && p.unitId === unitId) {
            const config = protocolConfigs[taskId];
            const group = p.groupId
                ? config?.reviewerGroups?.find((g: any) => g.id === p.groupId)
                : config?.reviewerGroups?.find((g: any) => g.unitIds.includes(p.unitId));

            if (group) {
                return group.reviewers.some((rev: any, idx: number) =>
                    rev.generatesProgress && p.signatures[idx]
                );
            }
        }
        return false;
    });
};

export const calculateTaskProgress = (
    taskId: string,
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: any,
    projectInfo: ProjectInfo,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[],
    tasks: Task[] = []
): number => {
    const taskObj = findTaskById(taskId, tasks);
    const parentTask = findParentTask(taskId, tasks);
    const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : taskId;
    const config = protocolConfigs[targetTaskId] || projectInfo.protocolConfigs?.[targetTaskId];
    const appliesProtocol = config?.applies ?? false;

    // 1. Get Total Assigned Units from EDT (Matrix)
    const assignedUnits = units.filter(u => matrixData[`planning_${targetTaskId}_${u.id}`] === '1');
    const total = assignedUnits.length;

    if (total === 0) return 0;

    // 2. Get Completed Units
    const completedUnitIds = new Set<string>();

    if (appliesProtocol) {
        // A. From Protocols with Progress Flag Signed (Automatic)
        assignedUnits.forEach(u => {
            if (isProtocolProgressed(targetTaskId, u.id, savedProtocols, protocolConfigs)) {
                completedUnitIds.add(u.id);
            }
        });
    } else {
        // B. From Weekly Plan manual execution
        weeklyPlans.forEach(plan => {
            plan.allocations.forEach(alloc => {
                if (alloc.taskId === taskId && alloc.executed) {
                    completedUnitIds.add(alloc.unitId);
                }
            });
        });
    }

    // 3. Valid Completed Units (Intersection)
    let validCompletedCount = 0;
    completedUnitIds.forEach(uid => {
        if (assignedUnits.some(u => u.id === uid)) {
            validCompletedCount++;
        }
    });

    return (validCompletedCount / total) * 100;
};

/**
 * Calculates the total project progress based on individual task completion.
 */
export const calculateProjectProgress = (
    tasks: Task[],
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: any,
    projectInfo: ProjectInfo,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[]
): number => {
    let totalWeightedProgress = 0;
    let totalDuration = 0;

    const traverse = (list: Task[]) => {
        list.forEach(t => {
            const hasSubtasks = t.children && t.children.some(c => c.isWeeklySubtask);
            if (t.type === 'task' && (!t.children || t.children.length === 0 || hasSubtasks)) {
                let progress = 0;
                if (hasSubtasks) {
                    const subtaskProgresses = t.children!.map(child => {
                        let physicalProgress = calculateTaskProgress(child.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                        return Math.min(100, (child.baseProgress !== undefined ? child.baseProgress : (child.progress || 0)) + physicalProgress);
                    });
                    progress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
                } else {
                    let physicalProgress = calculateTaskProgress(t.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                    progress = Math.min(100, (t.baseProgress !== undefined ? t.baseProgress : (t.progress || 0)) + physicalProgress);
                }

                const duration = t.duration || 0;
                totalWeightedProgress += (progress * duration);
                totalDuration += duration;
            } else if (t.children) {
                traverse(t.children);
            }
        });
    };

    traverse(tasks);
    return totalDuration > 0 ? (totalWeightedProgress / totalDuration) : 0;
};

/**
 * Calculates a duration-weighted global progress percentage based on task.progress property.
 * Useful for historical snapshots or when "Live" calculation is not needed.
 */
export const calculateWeightedProgress = (tasks: Task[]): number => {
    let totalWeightedProgress = 0;
    let totalDuration = 0;

    const traverse = (list: Task[]) => {
        list.forEach(t => {
            const hasSubtasks = t.children && t.children.some(c => c.isWeeklySubtask);
            if (t.type === 'task' && (!t.children || t.children.length === 0 || hasSubtasks)) {
                let progress = 0;
                if (hasSubtasks) {
                    const subtaskProgresses = t.children!.map(child => child.progress || 0);
                    progress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
                } else {
                    progress = t.progress || 0;
                }
                const duration = t.duration || 0;
                totalWeightedProgress += (progress * duration);
                totalDuration += duration;
            } else if (t.children) {
                traverse(t.children);
            }
        });
    };

    traverse(tasks);
    return totalDuration > 0 ? (totalWeightedProgress / totalDuration) : 0;
};

/**
 * Calculates how many days behind the real progress is compared to the planned curve.
 */
export const calculateDelayDays = (curveData: any[], currentRealPct: number, currentDate: string): number => {
    if (curveData.length === 0) return 0;

    // Si el avance real es 0 (o menor), comparamos la fecha actual contra el inicio programado del proyecto.
    // Si la fecha actual ya superó el inicio y seguimos en 0%, hay un retraso real igual a los días transcurridos.
    if (currentRealPct <= 0) {
        const sortedCurve = [...curveData].sort((a, b) => a.date.localeCompare(b.date));
        const projectStartTime = new Date(sortedCurve[0].date + "T12:00:00").getTime();
        const actualTime = new Date(currentDate + "T12:00:00").getTime();
        if (actualTime > projectStartTime) {
            const diffTime = actualTime - projectStartTime;
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
        return 0;
    }

    // Ensure sorted by date
    const sortedCurve = [...curveData].sort((a, b) => a.date.localeCompare(b.date));

    // Find the first week where the planned progress meets or exceeds our current real progress
    const targetIdx = sortedCurve.findIndex(p => p.plan >= currentRealPct);
    
    if (targetIdx === -1) {
        // Real progress is ahead of all planned points
        return 0;
    }

    let plannedTime: number;

    if (targetIdx === 0) {
        // It's before or exactly at the first week
        plannedTime = new Date(sortedCurve[0].date + "T12:00:00").getTime();
    } else {
        const pPrev = sortedCurve[targetIdx - 1];
        const pCurr = sortedCurve[targetIdx];
        
        const planPrev = pPrev.plan;
        const planCurr = pCurr.plan;
        
        const timePrev = new Date(pPrev.date + "T12:00:00").getTime();
        const timeCurr = new Date(pCurr.date + "T12:00:00").getTime();
        
        const planDiff = planCurr - planPrev;
        
        if (planDiff > 0) {
            const ratio = (currentRealPct - planPrev) / planDiff;
            plannedTime = timePrev + ratio * (timeCurr - timePrev);
        } else {
            plannedTime = timeCurr;
        }
    }

    const actualTime = new Date(currentDate + "T12:00:00").getTime();
    const diffTime = actualTime - plannedTime;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays); // Return positive delay
};

/**
 * Flattens a task tree into a flat array of tasks.
 */
export const flattenTasks = (list: Task[]): Task[] => {
    if (!list) return [];
    let flat: Task[] = [];
    list.forEach(t => {
        flat.push(t);
        if (t.children) {
            flat = flat.concat(flattenTasks(t.children));
        }
    });
    return flat;
};

/**
 * Finds a task by ID within a tree structure.
 */
export const findTaskById = (id: string | null, list: Task[]): Task | null => {
    if (!id || !list) return null;
    for (const t of list) {
        if (t.id === id) return t;
        if (t.children) {
            const found = findTaskById(id, t.children);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Finds the name of a task by ID within a tree structure.
 */
export const findTaskName = (id: string, list: Task[]): string => {
    if (!id || !list) return "";
    for (const t of list) {
        if (t.id === id) return t.name;
        if (t.children) {
            const found = findTaskName(id, t.children);
            if (found) return found;
        }
    }
    return "";
};

/**
 * Calculates the Critical Path Method (CPM) for a given set of tasks.
 * Marks `isCritical: true` on tasks that belong to the critical path (slack <= 0).
 */
export const calculateCriticalPath = (tasks: Task[]): Task[] => {
    // 1. Flatten all tasks
    const flatTasks = flattenTasks(tasks);
    if (flatTasks.length === 0) return tasks;

    // Filter to only consider leaf tasks (tasks without children)
    const leafTasks = flatTasks.filter(t => t.type === 'task' && (!t.children || t.children.length === 0));

    // Data structures for CPM
    const ef = new Map<string, number>(); // Early Finish
    const ls = new Map<string, number>(); // Late Start
    const lf = new Map<string, number>(); // Late Finish
    
    // Find absolute minimum time to convert dates to relative days
    let minTime = Number.MAX_SAFE_INTEGER;
    leafTasks.forEach(t => {
        if (t.start) {
            const time = new Date(t.start + "T12:00:00").getTime();
            if (time < minTime) minTime = time;
        }
    });
    
    // If no valid start dates are found, return the initial array
    if (minTime === Number.MAX_SAFE_INTEGER) return tasks; 

    const toDays = (dateStr: string) => {
        const t = new Date(dateStr + "T12:00:00").getTime();
        return Math.floor((t - minTime) / (1000 * 60 * 60 * 24));
    };

    // Forward pass equivalent: In this context, the user/Gantt already provides explicit start and end dates.
    // We treat the explicitly set `end` date as the Early Finish (EF) for the backward pass.
    leafTasks.forEach(t => {
        if (t.start && t.end) {
            ef.set(t.id, toDays(t.end));
        }
    });

    // Build successors map for the Backward Pass
    const successors = new Map<string, { id: string, type: string }[]>();
    leafTasks.forEach(t => {
        if (t.predecessors) {
            t.predecessors.forEach(predStr => {
                const match = predStr.match(/^(\d+)(FS|SS|FF|SF)?$/);
                if (match) {
                    const predId = match[1];
                    const type = match[2] || "FS";
                    if (!successors.has(predId)) successors.set(predId, []);
                    successors.get(predId)!.push({ id: t.id, type });
                }
            });
        }
    });

    // Find the max end date (project's maximum Early Finish)
    let maxEF = 0;
    ef.forEach((val) => {
        if (val > maxEF) maxEF = val;
    });

    // Backward pass: Initialize LF and LS for terminal tasks
    // Terminal tasks are those with no successors OR whose EF equals the project's maxEF
    leafTasks.forEach(t => {
        if (t.id && ef.has(t.id)) {
            const succs = successors.get(t.id) || [];
            if (succs.length === 0 || ef.get(t.id) === maxEF) {
                lf.set(t.id, maxEF);
                ls.set(t.id, maxEF - (t.duration || 1) + 1);
            }
        }
    });

    // Backward pass: Iterative relaxation for networks (handles cycles gracefully with an iteration limit)
    let changed = true;
    let iterations = 0;
    while(changed && iterations < 100) {
        changed = false;
        iterations++;
        leafTasks.forEach(t => {
            if (t.id && ef.has(t.id)) {
                const succs = successors.get(t.id) || [];
                let minLF = Number.MAX_SAFE_INTEGER;
                
                if (succs.length > 0) {
                    succs.forEach(succ => {
                        const succLS = ls.get(succ.id);
                        const succLF = lf.get(succ.id);
                        
                        if (succLS !== undefined && succLF !== undefined) {
                            let possibleLF = Number.MAX_SAFE_INTEGER;
                            if (succ.type === "FS") {
                                possibleLF = succLS - 1; 
                            } else if (succ.type === "SS") {
                                possibleLF = succLS + (t.duration || 1) - 1; 
                            } else if (succ.type === "FF") {
                                possibleLF = succLF; 
                            } else if (succ.type === "SF") {
                                possibleLF = succLF + (t.duration || 1) - 1; 
                            }
                            if (possibleLF < minLF) minLF = possibleLF;
                        }
                    });
                }
                
                if (minLF !== Number.MAX_SAFE_INTEGER) {
                    const currentLF = lf.get(t.id);
                    if (currentLF === undefined || minLF < currentLF) {
                        lf.set(t.id, minLF);
                        ls.set(t.id, minLF - (t.duration || 1) + 1);
                        changed = true;
                    }
                }
            }
        });
    }

    // Determine Critical tasks based on Slack = LF - EF <= 0
    const criticalTaskIds = new Set<string>();
    leafTasks.forEach(t => {
         if (t.id) {
             const tEF = ef.get(t.id);
             const tLF = lf.get(t.id);
             if (tEF !== undefined && tLF !== undefined) {
                 const slack = tLF - tEF;
                 if (slack <= 0) {
                     criticalTaskIds.add(t.id);
                 }
             }
         }
    });

    // Reconstruct tree and mark folders if any child is critical
    const applyCriticalRecursive = (list: Task[]): { tasks: Task[], hasCriticalChild: boolean } => {
        let anyChildCritical = false;
        const mappedList = list.map(t => {
            if (t.type === 'task' && (!t.children || t.children.length === 0)) {
                const isCrit = criticalTaskIds.has(t.id);
                if (isCrit) anyChildCritical = true;
                return { ...t, isCritical: isCrit };
            } else {
                const childRes = applyCriticalRecursive(t.children || []);
                if (childRes.hasCriticalChild) anyChildCritical = true;
                return { ...t, children: childRes.tasks, isCritical: childRes.hasCriticalChild };
            }
        });
        return { tasks: mappedList, hasCriticalChild: anyChildCritical };
    };

    return applyCriticalRecursive(tasks).tasks;
};

/**
 * Finds the parent task of a given task ID.
 */
export const findParentTask = (id: string | null, list: Task[]): Task | null => {
    if (!id || !list) return null;
    for (const t of list) {
        if (t.children && t.children.some(c => c.id === id)) {
            return t;
        }
        if (t.children) {
            const found = findParentTask(id, t.children);
            if (found) return found;
        }
    }
    return null;
};
